/**
 * ReviewRowLink — a queue-cell link that degrades to plain text (BON-381).
 *
 * The desktop rows carry two links out (the booking record, the workshop), and
 * a row can legitimately have no target for one of them: a flagged CHECKOUT has
 * no booking, because none was ever created. That renders as text, never as a
 * dead link — an anchor that goes nowhere is worse than a label, both for a
 * mouse and for a screen reader.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface ReviewRowLinkProps {
  /** Route to open, or null when this row has nothing to point at. */
  to: string | null;
  children: ReactNode;
}

/** A standing underline rather than a hover-only one: the rail's CSS is not this
 *  slice's to extend, and an affordance that only appears on hover is invisible
 *  to anyone navigating by keyboard or touch. */
const LINK_STYLE = {
  color: 'inherit',
  textDecoration: 'underline',
  textDecorationColor: 'var(--nb-border-2)',
  textDecorationThickness: 1,
  textUnderlineOffset: 2,
} as const;

export function ReviewRowLink({ to, children }: ReviewRowLinkProps) {
  if (!to) return <>{children}</>;
  return (
    <Link to={to} style={LINK_STYLE}>
      {children}
    </Link>
  );
}
